import { ExternalLink, Heart, Shield, Users, Award } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-gray-900/80 backdrop-blur-sm border-t border-gray-800 mt-12">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Brand Section */}
          <div className="md:col-span-2">
            <div className="flex items-center space-x-3 mb-4">
              <Shield className="w-8 h-8 text-cyan-400" />
              <h3 className="text-xl font-bold text-white">Insider Threat Matrix</h3>
            </div>
            <p className="text-gray-400 mb-4 max-w-md">
              The most comprehensive cybersecurity framework for detecting, analyzing, and preventing insider threats. 
              Trusted by security professionals worldwide to protect organizations from internal security risks.
            </p>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <span>Powered with</span>
              <Heart className="w-4 h-4 text-red-500 fill-current" />
              <span>by the</span>
              <a
                href="https://www.cisomarketplace.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-400 hover:text-cyan-300 transition-colors font-semibold"
              >
                CISO Marketplace
              </a>
              <span>ecosystem</span>
            </div>
          </div>

          {/* Security Tools Column 1 */}
          <div>
            <h4 className="text-white font-semibold mb-4 flex items-center">
              <Shield className="w-4 h-4 mr-2" />
              Security Tools
            </h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li>
                <a 
                  href="https://securitycareers.help" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-cyan-400 transition-colors flex items-center"
                >
                  Security Careers
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </li>
              <li>
                <a 
                  href="https://teamrisk.securitycareers.help/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-cyan-400 transition-colors flex items-center"
                >
                  Team Risk
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </li>
              <li>
                <a 
                  href="https://remotework.securitycareers.help/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-cyan-400 transition-colors flex items-center"
                >
                  Remote Work Risk
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </li>
              <li>
                <a 
                  href="https://insiderrisk.securitycareers.help/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-cyan-400 transition-colors flex items-center"
                >
                  Insider Risk
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </li>
              <li>
                <a 
                  href="https://maritime.securitycareers.help/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-cyan-400 transition-colors flex items-center"
                >
                  Maritime Security
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </li>
            </ul>
          </div>

          {/* Security Tools Column 2 */}
          <div>
            <h4 className="text-white font-semibold mb-4 flex items-center">
              <Users className="w-4 h-4 mr-2" />
              Risk Assessments
            </h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li>
                <a 
                  href="https://zerotrustciso.com/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-cyan-400 transition-colors flex items-center"
                >
                  Zero Trust CISO
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </li>
              <li>
                <a 
                  href="https://ratemysoc.com/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-cyan-400 transition-colors flex items-center"
                >
                  Rate My SOC
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </li>
              <li>
                <a 
                  href="https://phishingrisk.com/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-cyan-400 transition-colors flex items-center"
                >
                  Phishing Risk
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </li>
              <li>
                <a 
                  href="https://ransomwarematurity.com/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-cyan-400 transition-colors flex items-center"
                >
                  Ransomware Maturity
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </li>
              <li>
                <a 
                  href="https://cyberinsurancecalc.com/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-cyan-400 transition-colors flex items-center"
                >
                  Cyber Insurance Calc
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </li>
              <li>
                <a 
                  href="https://ssaephysicalsecurity.com/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-cyan-400 transition-colors flex items-center"
                >
                  Physical Security
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 border-t border-gray-800">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-6 text-sm text-gray-500">
              <span>© 2026 Insider Threat Matrix</span>
              <span>•</span>
              <span>Built for Security Professionals</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Award className="w-4 h-4" />
                <span>Trusted by 500+ Organizations</span>
              </div>
            </div>
          </div>
        </div>

        {/* SEO Keywords Footer */}
        <div className="mt-6 pt-4 border-t border-gray-800">
          <p className="text-xs text-gray-600 text-center leading-relaxed">
            Comprehensive insider threat detection | Enterprise cybersecurity framework | Security risk assessment tools | 
            CISO resources | Threat intelligence platform | Security career development | Cybersecurity training | 
            Insider threat prevention strategies | Security maturity assessment | Enterprise security solutions
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;